import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { authService, dashboardService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { useAuth } from "../../../context/AuthProvider";
import { getVisibleBots } from "../../../utils/bots";
import DisparoList from "./DisparoList";
import DisparoForm from "./DisparoForm";
import DisparoStats from "./DisparoStats";
import DisparoScheduleModal from "./DisparoScheduleModal";
import FormModal from "../../../components/UI/FormModal/FormModal";
import FiltersSection from "../../../components/Filter/Filter";
import {Sparkles} from 'lucide-react';


/* ── Page ── */
export default function DisparoPage() {
    const { user } = useAuth();
    const { showError, showSuccess } = useNotification();
    const ownBots = getVisibleBots(user?.bots);

    // ?bot=<id>: abre já filtrado nesse bot (atalho "Disparos" do painel admin).
    // Se o bot for de OUTRO usuário (admin), carrega os bots do dono para a
    // lista e o formulário funcionarem no escopo dele.
    const [searchParams, setSearchParams] = useSearchParams();
    const botParam = searchParams.get("bot");
    const botParamId = botParam && /^\d+$/.test(botParam) ? Number(botParam) : null;
    const [ownerCtx, setOwnerCtx] = useState(null); // { owner, bots } do dono do bot
    const isOwnParam = !!(botParamId && ownBots.some((b) => b.id === botParamId));
    const isForeign = !!(botParamId && user?.is_admin && !isOwnParam);
    // ?bot= só vale para bot próprio ou para admin; de outro usuário, é ignorado.
    const paramAllowed = isOwnParam || isForeign;
    useEffect(() => {
        setOwnerCtx(null);
        if (!isForeign) return;
        let cancelled = false;
        authService.bots.edit_bot(botParamId)
            .then(({ data }) => dashboardService.getAdminUserBots(data.bot.user_id))
            .then(({ data }) => { if (!cancelled) setOwnerCtx(data); })
            .catch(() => showError("Não foi possível carregar os bots do dono deste bot."));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isForeign, botParamId]);
    const bots = ownerCtx ? getVisibleBots(ownerCtx.bots) : ownBots;
    const focusBot = botParamId ? bots.find((b) => b.id === botParamId) : null;

    const [view, setView] = useState("list");
    const [editing, setEditing] = useState(null);
    const [statsFor, setStatsFor] = useState(null);
    const [statsRuns, setStatsRuns] = useState([]);
    const [scheduleFor, setScheduleFor] = useState(null);
    const [filterBotIds, setFilterBotIds] = useState(botParamId ? [botParamId] : null); // null = todos
    const [searchQuery, setSearchQuery] = useState('');

    const [disparos, setDisparos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0, pages: 0, current_page: 1, per_page: 10, has_next: false, has_prev: false,
    });
    const [currentPage, setCurrentPage] = useState(1);

    // Guarda contra o duplo-disparo do StrictMode (dev) e contra reload com os
    // mesmos parâmetros: só busca de novo quando (user, filterBotIds) muda de
    // fato — evita 2 requisições idênticas em sequência na montagem da página.
    const lastLoadKeyRef = useRef(null);
    useEffect(() => {
        if (!user) return;
        // ?bot= de bot alheio (não admin): descarta o parâmetro e o filtro antes de
        // carregar — a próxima execução carrega os disparos do próprio usuário.
        if (botParamId && !paramAllowed) {
            setSearchParams({}, { replace: true });
            if (filterBotIds) { setFilterBotIds(null); return; }
        }
        const key = JSON.stringify(filterBotIds);
        if (lastLoadKeyRef.current === key) return;
        lastLoadKeyRef.current = key;
        loadDisparos(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filterBotIds]);

    const loadDisparos = async (page = 1, search = searchQuery, silent = false) => {
        try {
            if (!silent) setLoading(true);
            const params = { page, per_page: 10 };
            if (filterBotIds?.length) params.bot_ids = filterBotIds.join(",");
            if (search?.trim()) params.search = search.trim();
            const { data } = await authService.disparo.list(params);
            if (data.success) {
                setDisparos(data.disparos);
                setPagination(data.pagination);
                setCurrentPage(page);
            }
        } catch {
            showError("Erro ao carregar disparos");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleEdit = (disparo) => {
        setEditing({
            ...disparo,
            plan_duration: disparo.plan_durations || disparo.plan_duration || [],
            plan_color: disparo.plan_color || disparo.plan_colors || [],
        });
        setView("form");
    };

    const handleNew = () => {
        setEditing(null);
        setView("form");
    };

    const handleStart = async (disparo) => {
        if (disparo.schedule) return;
        try {
            await authService.disparo.start(disparo.id);
            showSuccess("Disparo iniciado!");
            loadDisparos(currentPage, searchQuery, true);
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao iniciar disparo");
        }
    };

    const handleStop = async (disparo) => {
        try {
            await authService.disparo.stop(disparo.id);
            showSuccess("Interrupção solicitada. O disparo será pausado após o lote atual.");
            loadDisparos(currentPage, searchQuery, true);
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao parar disparo");
        }
    };

    const handleStats = async (disparo) => {
        setStatsFor(disparo);
        setStatsRuns([]);
        setView("stats");
        try {
            const { data } = await authService.disparo.runs(disparo.id);
            if (data.success) setStatsRuns(data.runs);
        } catch {
            showError("Erro ao carregar histórico de execuções");
        }
    };

    const handleSchedule = async ({ freq, onceDate, onceTime, time, customHours, intervalHours, weekdays }) => {
        // Converte horário BRT (UTC-3) para UTC somando 3h
        const brtToUtc = (t) => {
            const [h, m] = t.split(":").map(Number);
            return `${String((h + 3) % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        };

        let payload;
        if (freq === "once") {
            payload = { scheduled_for: `${onceDate}T${onceTime}:00-03:00` };
        } else if (freq === "daily") {
            payload = { recurrence: { type: "daily", time: brtToUtc(time) } };
        } else if (freq === "weekly") {
            const utcTime = brtToUtc(time);
            // Se BRT >= 21h, a conversão UTC cruza a meia-noite: o dia UTC é o DIA SEGUINTE ao BRT
            const brtHour = parseInt(time.split(":")[0]);
            const crossesMidnight = brtHour + 3 >= 24;
            const utcDays = crossesMidnight
                ? weekdays.map((d) => (d === 7 ? 1 : d + 1))
                : weekdays;
            payload = { recurrence: { type: "weekly", time: utcTime, days: utcDays } };
        } else if (freq === "custom") {
            payload = { recurrence: { type: "custom", hours: customHours.map(brtToUtc) } };
        } else if (freq === "interval") {
            payload = { recurrence: { type: "interval", interval_hours: intervalHours } };
        } else {
            showError("Frequência desconhecida");
            return;
        }

        try {
            await authService.disparo.schedule(scheduleFor.id, payload);
            showSuccess("Disparo agendado com sucesso!");
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao agendar disparo");
            return;
        }
        setScheduleFor(null);
        loadDisparos(currentPage);
    };

    const handleFormSuccess = () => {
        setEditing(null);
        setView("list");
        loadDisparos(1, searchQuery, true);
    };

    const handleCleanupCopies = async () => {
        if (!window.confirm("Arquivar todas as cópias com 0 envios? Esta ação não pode ser desfeita.")) return;
        try {
            const { data } = await authService.disparo.cleanupCopies();
            showSuccess(`${data.archived} cópia${data.archived !== 1 ? "s" : ""} arquivada${data.archived !== 1 ? "s" : ""}.`);
            loadDisparos(1);
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao limpar cópias");
        }
    };

    const handleDelete = async (disparo) => {
        if (!window.confirm(`Arquivar o disparo "${disparo.name}"? Ele não aparecerá mais na lista.`)) return;
        try {
            await authService.disparo.delete(disparo.id);
            showSuccess("Disparo arquivado.");
            setDisparos((prev) => prev.filter((d) => d.id !== disparo.id));
            setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao arquivar disparo");
        }
    };

    const handleApplyFilters = (filterData) => {
        if (filterData.botIds?.length) {
            setFilterBotIds(filterData.botIds);
            return;
        }
        // O filtro aplica "todos os bots" sozinho ao montar; com ?bot= válido na
        // URL, mantém o bot da URL (para ver todos: "Ver disparos de todos os bots").
        setFilterBotIds(botParamId && paramAllowed ? [botParamId] : null);
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setCurrentPage(1);
        loadDisparos(1, value);
    };

    const handleCancelSchedule = async (disparo) => {
        try {
            await authService.disparo.cancelSchedule(disparo.id);
            showSuccess("Agendamento cancelado.");
            loadDisparos(currentPage, searchQuery, true);
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao cancelar agendamento");
        }
    };

    const handleFormBack = () => {
        setEditing(null);
        setView("list");
    };

    return (
        <div className="page">
            {view !== "stats" && (
                <header className='mb-6 flex space-between flex flex-wrap flex-row'>
                    <div>
                        <h1 className="title">Disparos</h1>
                        <p className='text-gray-400'>Gerencie disparos para seus bots</p>
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                        {handleCleanupCopies && (
                            <button
                                onClick={handleCleanupCopies}
                                className="btn-secondary flex items-center gap-1.5 text-sm"
                                title="Arquivar cópias antigas sem envios"
                            >
                                <Sparkles size={14} />
                                Limpar Cópias
                            </button>
                        )}
                        <button onClick={handleNew} className="btn-primary">
                            <i className="fas fa-plus" /> Novo Disparo
                        </button>
                    </div>
                </header>
            )}

            {botParamId && paramAllowed && view !== "stats" && (
                <div className="mb-4 flex items-center justify-between gap-3 flex-wrap px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-sm">
                    <span className="text-white/70">
                        {isForeign && <><i className="fas fa-user-shield text-amber-300 mr-1.5" />Modo admin · {ownerCtx?.owner?.username ? `${ownerCtx.owner.username} · ` : ""}</>}
                        Disparos do bot <strong className="text-white">@{(focusBot?.bot_username || String(botParamId)).replace(/^@/, "")}</strong>
                    </span>
                    {!isForeign && (
                        <button
                            type="button"
                            className="text-xs text-[var(--brand-400)] hover:underline"
                            onClick={() => { setSearchParams({}); setFilterBotIds(null); }}
                        >
                            Ver disparos de todos os bots
                        </button>
                    )}
                </div>
            )}

            {/* FiltersSection sempre montado para preservar seleção de bot ao voltar do form */}
            <div style={{ display: view === "stats" ? "none" : undefined }}>
                {user && (
                    <FiltersSection
                        onApplyFilters={handleApplyFilters}
                        bots={bots}
                        gateway={[]}
                        showGatewayFilter={false}
                        showStatusFilter={false}
                        showFluxoFilter={false}
                        showStartsFilter={false}
                        showDateFilter={false}
                        enableBotMultiSelect={bots.length > 1}
                        compactBotFilter={true}
                        showBotFilter={bots.length > 1}
                        showInputFilter={true}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Buscar pelo nome do disparo..."
                    />
                )}

                <DisparoList
                    disparos={disparos}
                    bots={bots}
                    loading={loading}
                    onEdit={handleEdit}
                    onStart={handleStart}
                    onSchedule={(ds) => setScheduleFor(ds)}
                    onCancelSchedule={handleCancelSchedule}
                    onStats={handleStats}
                    onStop={handleStop}
                    onDelete={handleDelete}
                    pagination={pagination}
                    currentPage={currentPage}
                    onNextPage={() => pagination.has_next && loadDisparos(currentPage + 1)}
                    onPrevPage={() => pagination.has_prev && loadDisparos(currentPage - 1)}
                />
            </div>

            {/* FORM — modal de criar/editar disparo */}
            <FormModal open={view === "form"} onClose={handleFormBack} maxWidth={960}>
                <DisparoForm
                    bots={bots}
                    editing={editing}
                    onEditingChange={setEditing}
                    onBack={handleFormBack}
                    onSuccess={handleFormSuccess}
                />
            </FormModal>

            {view === "stats" && (
                <DisparoStats
                    disparo={statsFor}
                    runs={statsRuns}
                    onBack={() => setView("list")}
                />
            )}

            <DisparoScheduleModal
                open={!!scheduleFor}
                disparo={scheduleFor}
                onClose={() => setScheduleFor(null)}
                onSchedule={handleSchedule}
            />
        </div>
    );
}
